/**
 * Quantum-Safe Key Management System
 *
 * Provides secure key derivation, storage, and management for NIST post-quantum
 * cryptographic keys with hierarchical deterministic (HD) capabilities.
 */

import type {
  SecurityLevel,
  QuantumKeyPair,
  KeyDerivationPath,
  MasterKey,
  DerivedKeySet,
  KeyMetadata
} from '@/types/quantum';
import { toBufferSource, ensureArrayBuffer } from '@/utils/crypto';
import { NISTQuantumSafe } from '@/crypto/NISTQuantumSafe';
import { getKeyStorage } from '@/services/KeyStorage';

/**
 * Quantum-safe key derivation following BIP-32-like structure
 * but adapted for post-quantum algorithms
 */
export class QuantumKeyManager {
  private masterKey: MasterKey | null = null;
  private keyCache: Map<string, DerivedKeySet> = new Map();
  private securityLevel: SecurityLevel;
  private quantumSafe: NISTQuantumSafe;
  private keyStorage = getKeyStorage();

  constructor(securityLevel: SecurityLevel = 5) {
    this.securityLevel = securityLevel;
    this.quantumSafe = new NISTQuantumSafe(securityLevel);
  }

  /**
   * Generate a new master key from secure entropy
   *
   * @param entropy - Optional high-entropy seed (defaults to crypto.getRandomValues)
   * @returns Master key for hierarchical derivation
   */
  async generateMasterKey(entropy?: Uint8Array): Promise<MasterKey> {
    try {
      // Generate or use provided entropy
      const seed = entropy || crypto.getRandomValues(new Uint8Array(64)); // 512 bits

      // Generate additional entropy for quantum safety
      const quantumEntropy = crypto.getRandomValues(new Uint8Array(32));

      // Combine entropies using HKDF
      const combinedSeed = await this.combineEntropy(seed, quantumEntropy);

      // Derive master key using PBKDF2 with high iteration count
      const masterKeyMaterial = await crypto.subtle.importKey(
        'raw',
        toBufferSource(ensureArrayBuffer(combinedSeed)),
        { name: 'PBKDF2' },
        false,
        ['deriveBits']
      );

      const derivedBits = await crypto.subtle.deriveBits(
        {
          name: 'PBKDF2',
          salt: new TextEncoder().encode('QuantumSafeVault-MasterKey-v1'),
          iterations: 100000, // High iteration count for security
          hash: 'SHA-256'
        },
        masterKeyMaterial,
        512 // 64 bytes
      );

      const masterKeyData = new Uint8Array(derivedBits);

      this.masterKey = {
        keyData: masterKeyData,
        chainCode: masterKeyData.slice(32), // Second 32 bytes as chain code
        depth: 0,
        index: 0,
        parentFingerprint: new Uint8Array(4),
        securityLevel: this.securityLevel,
        timestamp: Date.now(),
        version: '1.0'
      };

      // Store master key persistently
      await this.keyStorage.storeMasterKey(
        masterKeyData,
        'Quantum-HD',
        this.securityLevel
      );

      console.log('QuantumKeyManager: Master key generated and stored persistently');
      return this.masterKey;
    } catch (error) {
      throw new Error(`Master key generation failed: ${error}`);
    }
  }

  /**
   * Restore master key from mnemonic seed phrase
   *
   * @param mnemonic - BIP-39 compatible mnemonic phrase
   * @param passphrase - Optional passphrase for additional security
   * @returns Restored master key
   */
  async restoreFromMnemonic(mnemonic: string, passphrase: string = ''): Promise<MasterKey> {
    try {
      // Convert mnemonic to seed (simplified version - production would use BIP-39)
      const mnemonicBytes = new TextEncoder().encode(mnemonic + passphrase);
      const seed = await crypto.subtle.digest('SHA-512', mnemonicBytes);

      return await this.generateMasterKey(new Uint8Array(seed));
    } catch (error) {
      throw new Error(`Mnemonic restoration failed: ${error}`);
    }
  }

  /**
   * Derive quantum-safe keys for specific Safe account
   *
   * @param safeAddress - Safe wallet address
   * @param purpose - Key purpose (e.g., 'encryption', 'signing')
   * @returns Complete set of derived quantum keys
   */
  async deriveSafeKeys(safeAddress: string, purpose: string = 'vault'): Promise<DerivedKeySet> {
    // Auto-initialize master key if not present
    await this.ensureMasterKeyInitialized();

    const derivationPath: KeyDerivationPath = {
      purpose: 44, // Standard BIP-44 purpose
      coinType: 60, // Ethereum coin type
      account: this.addressToAccountIndex(safeAddress),
      change: purpose === 'vault' ? 0 : 1,
      addressIndex: 0
    };

    const cacheKey = this.getDerivationCacheKey(derivationPath, purpose);

    // Check cache first
    if (this.keyCache.has(cacheKey)) {
      return this.keyCache.get(cacheKey)!;
    }

    try {
      // Ensure master key is initialized
      await this.ensureMasterKeyInitialized();

      // Derive intermediate key
      const intermediateKey = await this.deriveIntermediateKey(
        this.masterKey!,
        derivationPath
      );

      // Derive specific key types from intermediate key
      const kemSeed = await this.deriveTypedKey(intermediateKey, 'KEM');
      const dsaSeed = await this.deriveTypedKey(intermediateKey, 'DSA');
      const slhSeed = await this.deriveTypedKey(intermediateKey, 'SLH');

      // Generate quantum key pairs from seeds
      const kemKeys = await this.generateKeysFromSeed(kemSeed, 'KEM');
      const dsaKeys = await this.generateKeysFromSeed(dsaSeed, 'DSA');
      const slhKeys = await this.generateKeysFromSeed(slhSeed, 'SLH');

      const derivedKeySet: DerivedKeySet = {
        kemKeys,
        dsaKeys,
        slhKeys,
        derivationPath,
        safeAddress,
        purpose,
        securityLevel: this.securityLevel,
        timestamp: Date.now(),
        metadata: {
          version: '1.0',
          algorithm: 'Quantum-HD',
          parentFingerprint: this.getKeyFingerprint(this.masterKey!.keyData)
        }
      };

      // Cache the derived keys
      this.keyCache.set(cacheKey, derivedKeySet);

      return derivedKeySet;
    } catch (error) {
      throw new Error(`Key derivation failed: ${error}`);
    }
  }

  /**
   * Derive keys for asset encryption/decryption
   *
   * @param safeAddress - Safe wallet address
   * @param assetIdentifier - Unique asset identifier (token address + ID)
   * @returns Asset-specific encryption keys
   */
  async deriveAssetKeys(safeAddress: string, assetIdentifier: string): Promise<DerivedKeySet> {
    // Auto-initialize master key if not present
    await this.ensureMasterKeyInitialized();

    const assetHash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(assetIdentifier));
    const assetIndex = new DataView(assetHash).getUint32(0, true) % 2147483648; // Ensure positive 31-bit

    const derivationPath: KeyDerivationPath = {
      purpose: 44,
      coinType: 60,
      account: this.addressToAccountIndex(safeAddress),
      change: 2, // Asset-specific change index
      addressIndex: assetIndex
    };

    return await this.deriveKeysFromPath(derivationPath, `asset:${assetIdentifier}`);
  }

  /**
   * Generate mnemonic phrase for master key backup
   *
   * @returns 24-word mnemonic phrase (simplified - production would use BIP-39)
   */
  async generateMnemonic(): Promise<string> {
    if (!this.masterKey) {
      throw new Error('Master key not initialized');
    }

    // Simplified mnemonic generation (production would use proper BIP-39 wordlist)
    const entropy = this.masterKey.keyData.slice(0, 32);
    const words: string[] = [];

    // Convert entropy to pseudo-words (simplified)
    for (let i = 0; i < 24; i++) {
      const wordIndex = entropy[i % 32] * 256 + entropy[(i + 1) % 32];
      words.push(`word${wordIndex.toString(16)}`);
    }

    return words.join(' ');
  }

  /**
   * Export keys for secure backup
   *
   * @param password - Encryption password for backup
   * @returns Encrypted backup data
   */
  async exportEncryptedBackup(password: string): Promise<Uint8Array> {
    if (!this.masterKey) {
      throw new Error('Master key not initialized');
    }

    try {
      // Derive encryption key from password
      const passwordKey = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveBits']
      );

      const encryptionKey = await crypto.subtle.deriveBits(
        {
          name: 'PBKDF2',
          salt: new TextEncoder().encode('QuantumSafeVault-Backup-Salt'),
          iterations: 100000,
          hash: 'SHA-256'
        },
        passwordKey,
        256
      );

      const aesKey = await crypto.subtle.importKey(
        'raw',
        encryptionKey,
        { name: 'AES-GCM' },
        false,
        ['encrypt']
      );

      // Prepare backup data
      const backupData = JSON.stringify({
        masterKey: Array.from(this.masterKey.keyData),
        securityLevel: this.securityLevel,
        timestamp: this.masterKey.timestamp,
        version: this.masterKey.version
      });

      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encryptedData = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        aesKey,
        new TextEncoder().encode(backupData)
      );

      // Combine IV and encrypted data
      const backup = new Uint8Array(iv.length + encryptedData.byteLength);
      backup.set(iv);
      backup.set(new Uint8Array(encryptedData), iv.length);

      return backup;
    } catch (error) {
      throw new Error(`Backup export failed: ${error}`);
    }
  }

  /**
   * Import keys from encrypted backup
   *
   * @param backupData - Encrypted backup data
   * @param password - Decryption password
   */
  async importEncryptedBackup(backupData: Uint8Array, password: string): Promise<void> {
    try {
      // Derive decryption key from password
      const passwordKey = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveBits']
      );

      const decryptionKey = await crypto.subtle.deriveBits(
        {
          name: 'PBKDF2',
          salt: new TextEncoder().encode('QuantumSafeVault-Backup-Salt'),
          iterations: 100000,
          hash: 'SHA-256'
        },
        passwordKey,
        256
      );

      const aesKey = await crypto.subtle.importKey(
        'raw',
        decryptionKey,
        { name: 'AES-GCM' },
        false,
        ['decrypt']
      );

      // Extract IV and encrypted data
      const iv = backupData.slice(0, 12);
      const encryptedData = backupData.slice(12);

      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        aesKey,
        encryptedData
      );

      const backupJson = new TextDecoder().decode(decryptedBuffer);
      const backup = JSON.parse(backupJson);

      // Restore master key
      this.masterKey = {
        keyData: new Uint8Array(backup.masterKey),
        chainCode: new Uint8Array(backup.masterKey).slice(32),
        depth: 0,
        index: 0,
        parentFingerprint: new Uint8Array(4),
        securityLevel: backup.securityLevel,
        timestamp: backup.timestamp,
        version: backup.version
      };

      this.securityLevel = backup.securityLevel;

      // Clear cache after import
      this.keyCache.clear();
    } catch (error) {
      throw new Error(`Backup import failed: ${error}`);
    }
  }

  /**
   * Get key metadata for UI display
   */
  getKeyMetadata(): KeyMetadata | null {
    if (!this.masterKey) {
      return null;
    }

    return {
      hasKeys: true,
      securityLevel: this.securityLevel,
      keyCount: this.keyCache.size,
      masterKeyTimestamp: this.masterKey.timestamp,
      lastUsed: Math.max(...Array.from(this.keyCache.values()).map(k => k.timestamp)),
      version: this.masterKey.version
    };
  }

  /**
   * Clear all keys from memory (for security)
   */
  clearKeys(): void {
    this.masterKey = null;
    this.keyCache.clear();
  }


  /**
   * Ensure master key is initialized, loading from storage or generating if needed
   */
  private async ensureMasterKeyInitialized(): Promise<void> {
    if (!this.masterKey) {
      console.log('QuantumKeyManager: Master key not in memory, checking storage...');

      // Try to load from persistent storage first
      const storedKeyData = await this.keyStorage.getMasterKey();

      if (storedKeyData) {
        console.log('QuantumKeyManager: Found stored master key, loading...');
        this.masterKey = {
          keyData: storedKeyData,
          chainCode: storedKeyData.slice(32),
          depth: 0,
          index: 0,
          parentFingerprint: new Uint8Array(4),
          securityLevel: this.securityLevel,
          timestamp: Date.now(),
          version: '1.0'
        };
        console.log('QuantumKeyManager: Master key loaded from storage successfully');
      } else {
        console.log('QuantumKeyManager: No stored master key found, generating new key...');
        await this.generateMasterKey();
        console.log('QuantumKeyManager: Master key generated successfully');
      }
    }
  }

  // Private helper methods

  private async combineEntropy(seed1: Uint8Array, seed2: Uint8Array): Promise<Uint8Array> {
    const combined = new Uint8Array(seed1.length + seed2.length);
    combined.set(seed1);
    combined.set(seed2, seed1.length);

    const hash = await crypto.subtle.digest('SHA-512', combined);
    return new Uint8Array(hash);
  }

  private async deriveIntermediateKey(parent: MasterKey, path: KeyDerivationPath): Promise<Uint8Array> {
    // Simplified key derivation (production would follow BIP-32 exactly)
    const pathData = new TextEncoder().encode(
      `m/${path.purpose}'/${path.coinType}'/${path.account}'/${path.change}/${path.addressIndex}`
    );

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      toBufferSource(ensureArrayBuffer(parent.keyData)),
      { name: 'HKDF' },
      false,
      ['deriveBits']
    );

    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: toBufferSource(ensureArrayBuffer(parent.chainCode)),
        info: pathData
      },
      keyMaterial,
      512
    );

    return new Uint8Array(derivedBits);
  }

  private async deriveTypedKey(intermediateKey: Uint8Array, keyType: 'KEM' | 'DSA' | 'SLH'): Promise<Uint8Array> {
    const typeInfo = new TextEncoder().encode(`QuantumSafe-${keyType}-Key`);

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      toBufferSource(ensureArrayBuffer(intermediateKey)),
      { name: 'HKDF' },
      false,
      ['deriveBits']
    );

    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: new Uint8Array(32),
        info: typeInfo
      },
      keyMaterial,
      512
    );

    return new Uint8Array(derivedBits);
  }

  private async generateKeysFromSeed(_seed: Uint8Array, algorithm: 'KEM' | 'DSA' | 'SLH'): Promise<QuantumKeyPair> {
    try {
      // Generate real NIST post-quantum keys
      // NOTE: Real NIST implementations use internal entropy, seed is used for derivation path

      switch (algorithm) {
        case 'KEM': {
          const kemKeys = this.quantumSafe.generateKEMKeys();
          return {
            publicKey: kemKeys.publicKey,
            privateKey: kemKeys.privateKey,
            algorithm: kemKeys.algorithm,
            securityLevel: this.securityLevel,
            keySize: kemKeys.keySize,
            timestamp: Date.now()
          };
        }
        case 'DSA': {
          const dsaKeys = this.quantumSafe.generateDSAKeys();
          return {
            publicKey: dsaKeys.publicKey,
            privateKey: dsaKeys.privateKey,
            algorithm: dsaKeys.algorithm,
            securityLevel: this.securityLevel,
            keySize: dsaKeys.keySize,
            timestamp: Date.now()
          };
        }
        case 'SLH': {
          const slhKeys = this.quantumSafe.generateSLHKeys();
          return {
            publicKey: slhKeys.publicKey,
            privateKey: slhKeys.privateKey,
            algorithm: slhKeys.algorithm,
            securityLevel: this.securityLevel,
            keySize: slhKeys.keySize,
            timestamp: Date.now()
          };
        }
        default:
          throw new Error(`Unsupported algorithm: ${algorithm}`);
      }
    } catch (error) {
      throw new Error(`Key generation failed for ${algorithm}: ${error}`);
    }
  }

  private async deriveKeysFromPath(path: KeyDerivationPath, purpose: string): Promise<DerivedKeySet> {
    if (!this.masterKey) {
      throw new Error('Master key not initialized');
    }

    const cacheKey = this.getDerivationCacheKey(path, purpose);

    if (this.keyCache.has(cacheKey)) {
      return this.keyCache.get(cacheKey)!;
    }

    const intermediateKey = await this.deriveIntermediateKey(this.masterKey, path);

    const kemSeed = await this.deriveTypedKey(intermediateKey, 'KEM');
    const dsaSeed = await this.deriveTypedKey(intermediateKey, 'DSA');
    const slhSeed = await this.deriveTypedKey(intermediateKey, 'SLH');

    const kemKeys = await this.generateKeysFromSeed(kemSeed, 'KEM');
    const dsaKeys = await this.generateKeysFromSeed(dsaSeed, 'DSA');
    const slhKeys = await this.generateKeysFromSeed(slhSeed, 'SLH');

    const derivedKeySet: DerivedKeySet = {
      kemKeys,
      dsaKeys,
      slhKeys,
      derivationPath: path,
      safeAddress: '', // Will be set by caller
      purpose,
      securityLevel: this.securityLevel,
      timestamp: Date.now(),
      metadata: {
        version: '1.0',
        algorithm: 'Quantum-HD',
        parentFingerprint: this.getKeyFingerprint(this.masterKey.keyData)
      }
    };

    this.keyCache.set(cacheKey, derivedKeySet);
    return derivedKeySet;
  }

  private addressToAccountIndex(address: string): number {
    // Convert Safe address to deterministic account index
    const hash = address.toLowerCase().slice(2); // Remove 0x prefix
    return parseInt(hash.slice(0, 8), 16) % 2147483648; // Ensure positive 31-bit
  }

  private getDerivationCacheKey(path: KeyDerivationPath, purpose: string): string {
    return `${path.purpose}-${path.coinType}-${path.account}-${path.change}-${path.addressIndex}-${purpose}`;
  }

  private getKeyFingerprint(keyData: Uint8Array): string {
    // Simple fingerprint - production would use proper key fingerprinting
    let hash = 0;
    for (let i = 0; i < Math.min(keyData.length, 20); i++) {
      hash = ((hash << 5) - hash + keyData[i]) & 0xffffffff;
    }
    return hash.toString(16).padStart(8, '0');
  }
}